import { Skeleton } from "@hakistudio/hakiui";
import { NOTE_TEMPLATES, noteTemplate, type NoteTemplate, type NoteType } from "@sebas/core";
import { Mic, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_SCOPE,
  ScopePicker,
  type NoteScopeSelection,
} from "@/components/notes/ScopePicker";
import { TemplateIcon, TemplatePicker } from "@/components/notes/TemplatePicker";
import { Chip } from "@/components/ui/Chip";
import { NoteTile } from "@/components/ui/NoteTile";
import { useCreateNote, useNotes, useTags, type NoteFilters } from "@/lib/queries";
import { Input } from "@/components/ui/Field";

type FilterId = "all" | "pinned" | "archived";

/** Blank is not a filter — every note that is only text would match it. */
const TYPE_CHIPS = NOTE_TEMPLATES.filter((t) => t.id !== "plain");

export function NotesPage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<NoteScopeSelection>(DEFAULT_SCOPE);
  const [filter, setFilter] = useState<FilterId>("all");
  const [tagId, setTagId] = useState<string | undefined>();
  const [noteType, setNoteType] = useState<NoteType | undefined>();
  const [q, setQ] = useState("");
  const [picking, setPicking] = useState(false);

  const filters: NoteFilters = useMemo(
    () => ({
      q: q.trim() || undefined,
      scope: scope.scope,
      teamId: scope.teamId,
      tagId,
      noteType,
      archived: filter === "archived",
      ...(filter === "pinned" ? { pinned: true } : {}),
    }),
    [q, scope, tagId, noteType, filter],
  );

  const { data, isPending } = useNotes(filters);
  const { data: tagData } = useTags();
  const create = useCreateNote();

  /** Clearing the other two keeps the chip row honest: one filter at a time. */
  const only = (next: { filter?: FilterId; tagId?: string; noteType?: NoteType }) => {
    setFilter(next.filter ?? "all");
    setTagId(next.tagId);
    setNoteType(next.noteType);
  };

  // The chip row scrolls sideways on a phone; keep the selected chip in view so
  // picking "Shopping list" from the hidden end doesn't leave it half-cut.
  const chipRow = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const active = chipRow.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    active?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [filter, tagId, noteType]);

  /**
   * New notes land in whatever library is on screen.
   *
   * Only a team scope carries a home of its own — "shared with me" and "all"
   * are views across other people's notes, not places to put one, so those fall
   * back to personal.
   */
  const homeTeamId = scope.scope === "team" ? scope.teamId : undefined;

  const startFrom = (template: NoteTemplate) =>
    create.mutate(
      {
        title: template.title(new Date()),
        content: template.content,
        color: template.color,
        noteType: template.id,
        teamId: homeTeamId,
      },
      {
        onSuccess: (res) => {
          setPicking(false);
          navigate(`/notes/${res.note.id}`);
        },
      },
    );

  const notes = data?.notes ?? [];

  return (
    <div className="flex flex-col gap-6">
      <ScopePicker value={scope} onChange={setScope} />

      <Input
value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search your notes"
        startContent={<Search className="h-4 w-4 text-ink-faint" />}
      />

      <div
        ref={chipRow}
        className="-mx-5 flex scroll-px-5 gap-2 overflow-x-auto px-5 pb-1 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <Chip
          active={filter === "all" && !tagId && !noteType}
          onClick={() => only({})}
        >
          All
        </Chip>
        <Chip active={filter === "pinned"} onClick={() => only({ filter: "pinned" })}>
          Pinned
        </Chip>
        <Chip active={filter === "archived"} onClick={() => only({ filter: "archived" })}>
          Archived
        </Chip>
        {TYPE_CHIPS.map((t) => (
          <Chip
            key={t.id}
            active={noteType === t.id}
            onClick={() => only({ noteType: t.id })}
          >
            <TemplateIcon icon={t.icon} className="h-3.5 w-3.5" />
            {t.label}
          </Chip>
        ))}
        {(tagData?.tags ?? []).map((t) => (
          <Chip key={t.id} active={tagId === t.id} onClick={() => only({ tagId: t.id })}>
            {t.name}
          </Chip>
        ))}
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="9rem" radius="lg" />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded-card border border-dashed border-line px-6 py-14 text-center">
          <p className="font-display text-2xl font-semibold">
            {q
              ? "Nothing matched"
              : filter === "archived"
                ? "No archived notes"
                : scope.scope === "shared"
                  ? "Nothing shared with you"
                  : "No notes yet"}
          </p>
          <p className="mt-2 text-sm text-ink-muted">
            {q
              ? "Try a different word."
              : scope.scope === "shared"
                ? "Notes people share with you appear here once you accept them."
                : "Press ⌘N, or use the button below, to start one."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {notes.map((n) => (
            <button key={n.id} className="text-left" onClick={() => navigate(`/notes/${n.id}`)}>
              <NoteTile
                title={n.title || "Untitled"}
                meta={new Date(n.updatedAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
                color={n.color}
                pinned={n.isPinned}
                icon={
                  n.noteType === "plain" ? null : (
                    <TemplateIcon icon={noteTemplate(n.noteType).icon} className="h-4 w-4" />
                  )
                }
              >
                {n.preview ? (
                  <p className="line-clamp-4 whitespace-pre-line text-sm text-tile-ink/70">
                    {n.preview}
                  </p>
                ) : null}
              </NoteTile>
            </button>
          ))}
        </div>
      )}

      <div className="fixed bottom-24 right-5 z-30 flex items-center gap-2 lg:bottom-10 lg:right-10">
        <button
          type="button"
          aria-label="Record audio note"
          onClick={() =>
            create.mutate(
              { teamId: homeTeamId },
              { onSuccess: (r) => navigate(`/notes/${r.note.id}?record=1`) },
            )
          }
          className="grid h-14 w-14 place-items-center rounded-full bg-raised text-ink shadow-card"
        >
          <Mic className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="New note"
          onClick={() => setPicking(true)}
          className="grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-ink shadow-card"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      {picking ? (
        <TemplatePicker
          busy={create.isPending}
          onPick={startFrom}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>
  );
}
